import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Search, ShoppingBag, X, ExternalLink, Loader2, RotateCcw } from "lucide-react";
import api, { inr, fmtDate, formatApiError } from "./api";
import { PageHeader, StatusBadge, Progress, EmptyState, inputCls } from "./components";
import { EASE } from "@/components/Motion";

const TABS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "in_progress", label: "In Progress" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
  { id: "partial", label: "Partial" },
  { id: "refunded", label: "Refunded" },
];

export default function OrdersPage() {
  const [orders, setOrders] = useState(null);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [refundReason, setRefundReason] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  const requestCancel = async () => {
    setCancelBusy(true);
    try {
      await api.post(`/orders/${selected.order_id}/cancel-request`);
      setSelected((s) => ({ ...s, cancel_requested: true }));
      setOrders((o) => o.map((x) => (x.order_id === selected.order_id ? { ...x, cancel_requested: true } : x)));
      setCancelConfirm(false);
      toast.success("Cancellation in process", { description: "Order will be cancelled within 0–24 hours; refund will be credited as per the refund policy." });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelBusy(false);
    }
  };

  const loadRefunds = () => api.get("/refunds/mine").then(({ data }) => setRefunds(data)).catch(() => {});

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
    loadRefunds();
  }, []);

  const requestRefund = async () => {
    if (refundReason.trim().length < 5) return;
    setRefundBusy(true);
    try {
      await api.post("/refunds", { order_id: selected.order_id, reason: refundReason.trim() });
      toast.success("Refund requested", { description: "Admin will review it — per the 0–45 working day policy." });
      setRefundReason("");
      loadRefunds();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefundBusy(false);
    }
  };

  const counts = useMemo(() => {
    const c = { all: orders?.length || 0 };
    TABS.slice(1).forEach((t) => (c[t.id] = 0));
    (orders || []).forEach((o) => (c[o.status] = (c[o.status] || 0) + 1));
    return c;
  }, [orders]);

  const filtered = useMemo(() => {
    if (!orders) return [];
    const q = search.trim().toLowerCase();
    return orders.filter(
      (o) =>
        (tab === "all" || o.status === tab) &&
        (!q ||
          o.order_id.toLowerCase().includes(q) ||
          o.service_name.toLowerCase().includes(q) ||
          o.link.toLowerCase().includes(q))
    );
  }, [orders, tab, search]);

  return (
    <div data-testid="orders-page">
      <PageHeader title="My Orders" sub="Every unit of work, tracked to the last digit." />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`orders-tab-${t.id.replace("_", "-")}`}
            className={`rounded-full border px-4 py-2 text-xs font-medium transition-all duration-300 ${
              tab === t.id
                ? "border-gold bg-gold/10 text-goldlight"
                : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-neutral-100"
            }`}
          >
            {t.label}
            <span className="ml-2 font-mono text-[9px] text-neutral-500">{counts[t.id] || 0}</span>
          </button>
        ))}
      </div>

      <div className="relative mb-6 max-w-md">
        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by order ID, service or link"
          className={`${inputCls} pl-11`}
          data-testid="orders-search-input"
        />
      </div>

      {orders === null ? (
        <div className="flex justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={ShoppingBag} title="No orders here yet" sub="Place your first order and watch this fill up." testid="orders-empty" />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="overflow-x-auto rounded-2xl border border-white/10"
          data-testid="orders-table"
        >
          <div className="min-w-[900px]">
            <div className="grid grid-cols-12 gap-3 border-b border-white/10 bg-white/[0.02] px-5 py-3 font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">
              <span className="col-span-1">ID</span>
              <span className="col-span-3">Service</span>
              <span className="col-span-2">Link</span>
              <span className="col-span-1">Qty</span>
              <span className="col-span-1">Charge</span>
              <span className="col-span-1">Status</span>
              <span className="col-span-1">Date</span>
              <span className="col-span-1">Progress</span>
              <span className="col-span-1 text-right">View</span>
            </div>
            {filtered.map((o) => (
              <div
                key={o.id}
                data-testid={`order-row-${o.order_id}`}
                className="grid grid-cols-12 items-center gap-3 border-b border-white/5 bg-panel/80 px-5 py-3.5 transition-colors duration-300 last:border-0 hover:bg-gold/[0.04]"
              >
                <span className="col-span-1 font-mono text-xs text-gold/80">{o.order_id}</span>
                <span className="col-span-3 truncate text-sm text-neutral-200" title={o.service_name}>{o.service_name}</span>
                <span className="col-span-2 truncate font-mono text-xs text-neutral-500" title={o.link}>{o.link}</span>
                <span className="col-span-1 text-sm text-neutral-300">{o.quantity.toLocaleString("en-IN")}</span>
                <span className="col-span-1 text-sm font-medium text-neutral-200">{inr(o.charge)}</span>
                <span className="col-span-1">
                  <StatusBadge status={o.status} />
                  {o.cancel_requested && !["cancelled", "refunded", "completed"].includes(o.status) && (
                    <span className="mt-1 inline-block rounded-full bg-amber-400/15 px-1.5 py-0.5 font-mono text-[8px] tracking-wide text-amber-300" data-testid={`order-cancelling-${o.order_id}`}>CANCELLING</span>
                  )}
                </span>
                <span className="col-span-1 font-mono text-[10px] text-neutral-500">{fmtDate(o.created_at)}</span>
                <span className="col-span-1"><Progress value={o.progress} /></span>
                <span className="col-span-1 text-right">
                  <button
                    onClick={() => setSelected(o)}
                    data-testid={`order-view-${o.order_id}`}
                    className="rounded-full border border-gold/30 px-3 py-1.5 text-[11px] font-semibold text-goldlight transition-all duration-300 hover:bg-gold hover:text-black"
                  >
                    Details
                  </button>
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="w-full max-w-lg rounded-3xl border border-gold/20 bg-panel p-8"
              onClick={(e) => e.stopPropagation()}
              data-testid="order-details-modal"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">ORDER DETAILS</div>
                  <h3 className="mt-2 text-2xl font-bold text-neutral-50">{selected.order_id}</h3>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  data-testid="order-modal-close"
                  aria-label="Close"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-neutral-400 transition-colors hover:border-gold/50 hover:text-neutral-100"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="mt-6 space-y-4">
                {[
                  ["Service", selected.service_name],
                  ["Quantity", selected.quantity.toLocaleString("en-IN")],
                  ["Charge", inr(selected.charge)],
                  ["Start count", selected.start_count != null ? selected.start_count.toLocaleString("en-IN") : "—"],
                  ["Current count", selected.current_count != null ? selected.current_count.toLocaleString("en-IN") : "—"],
                  ["Placed", fmtDate(selected.created_at)],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b border-white/5 pb-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{k}</span>
                    <span className="text-sm font-medium text-neutral-100">{v}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between border-b border-white/5 pb-3" data-testid="order-eta-row">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Estimated delivery</span>
                  <span className="text-sm font-medium text-neutral-100">{selected.eta ? fmtDate(selected.eta) : "—"}</span>
                </div>
                <div className="flex items-center justify-between border-b border-white/5 pb-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Status</span>
                  <StatusBadge status={selected.status} />
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Progress</span>
                    <span className="font-mono text-xs text-gold">{selected.progress}%</span>
                  </div>
                  <Progress value={selected.progress} />
                </div>
                <a
                  href={selected.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="order-modal-link"
                  className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 font-mono text-xs text-neutral-400 transition-colors hover:border-gold/40 hover:text-goldlight"
                >
                  <span className="truncate">{selected.link}</span>
                  <ExternalLink size={13} className="shrink-0" />
                </a>

                {["pending", "in_progress", "partial"].includes(selected.status) && (
                  selected.cancel_requested ? (
                    <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3" data-testid="cancel-process-box">
                      <p className="font-mono text-[10px] font-bold tracking-[0.18em] text-amber-300">CANCELLATION IN PROCESS</p>
                      <p className="mt-1.5 text-[11px] leading-relaxed text-amber-200/80">
                        Your cancellation request is in process — the order will be cancelled within 0–24 hours
                        and the amount will be credited to your wallet as per the refund policy.
                      </p>
                      {selected.cancel_progress != null && (
                        <div className="mt-3" data-testid="cancel-progress-bar">
                          <div className="flex items-center justify-between font-mono text-[9px] tracking-[0.15em] text-amber-300/90">
                            <span>PROGRESS</span>
                            <span data-testid="cancel-progress-pct">{selected.cancel_progress}%</span>
                          </div>
                          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-black/30">
                            <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all duration-700"
                              style={{ width: `${selected.cancel_progress}%` }} />
                          </div>
                          {selected.cancel_progress_note ? (
                            <p className="mt-2 text-[11px] italic leading-relaxed text-amber-200/80" data-testid="cancel-progress-note">
                              “{selected.cancel_progress_note}”
                            </p>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Changed your mind?</label>
                      {cancelConfirm ? (
                        <div className="mt-3 flex gap-2">
                          <button onClick={requestCancel} disabled={cancelBusy} data-testid="cancel-confirm-button"
                            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-rose-400/90 py-2.5 text-xs font-bold text-black transition-opacity hover:opacity-90 disabled:opacity-40">
                            {cancelBusy ? <Loader2 size={13} className="animate-spin" /> : "YES, CANCEL ORDER"}
                          </button>
                          <button onClick={() => setCancelConfirm(false)} data-testid="cancel-abort-button"
                            className="flex-1 rounded-xl border border-white/10 py-2.5 text-xs font-semibold text-neutral-400 transition-colors hover:border-white/25">
                            KEEP ORDER
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setCancelConfirm(true)} data-testid="cancel-order-button"
                          className="mt-3 w-full rounded-xl border border-rose-400/40 py-2.5 text-xs font-semibold text-rose-300 transition-colors hover:bg-rose-400/10">
                          CANCEL THIS ORDER
                        </button>
                      )}
                      <p className="mt-2 font-mono text-[9px] tracking-[0.15em] text-neutral-600">
                        PROCESSED WITHIN 0–24 HOURS · REFUND AS PER POLICY
                      </p>
                    </div>
                  )
                )}

                {["cancelled", "refunded"].includes(selected.status) && (
                  <div className="rounded-xl border border-rose-400/25 bg-rose-400/[0.07] px-4 py-3" data-testid="cancel-done-box">
                    <p className="font-mono text-[10px] font-bold tracking-[0.18em] text-rose-300">
                      ORDER {selected.status.toUpperCase()} — 100%
                    </p>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/30">
                      <div className="h-full w-full rounded-full bg-gradient-to-r from-rose-500 to-rose-300" />
                    </div>
                    <p className="mt-2 text-[11px] leading-relaxed text-rose-200/70">
                      Amount {selected.status === "refunded" ? "refunded" : "credited"} to your wallet as per the refund policy.
                    </p>
                  </div>
                )}

                {selected.status !== "refunded" && (() => {
                  const req = refunds.find((r) => r.order_id === selected.order_id);
                  if (req)
                    return (
                      <div
                        className={`rounded-xl border px-4 py-3 text-xs font-medium ${
                          req.status === "approved"
                            ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                            : req.status === "rejected"
                              ? "border-rose-400/30 bg-rose-400/10 text-rose-300"
                              : "border-amber-400/30 bg-amber-400/10 text-amber-300"
                        }`}
                        data-testid="refund-status-box"
                      >
                        Refund request {req.status}
                        {req.review_note ? ` — ${req.review_note}` : ""}
                      </div>
                    );
                  return (
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                      <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                        <RotateCcw size={12} className="text-gold/70" /> Request a refund
                      </label>
                      <div className="mt-3 flex gap-2">
                        <input
                          value={refundReason}
                          onChange={(e) => setRefundReason(e.target.value)}
                          placeholder="Reason (min. 5 characters)"
                          className={inputCls}
                          data-testid="refund-reason-input"
                        />
                        <button
                          onClick={requestRefund}
                          disabled={refundBusy || refundReason.trim().length < 5}
                          data-testid="refund-request-button"
                          className="flex shrink-0 items-center gap-2 rounded-xl border border-gold/40 px-4 text-xs font-semibold text-goldlight transition-all duration-300 hover:bg-gold hover:text-black disabled:opacity-40"
                        >
                          {refundBusy ? <Loader2 size={13} className="animate-spin" /> : "Request"}
                        </button>
                      </div>
                      <p className="mt-2 font-mono text-[9px] tracking-[0.15em] text-neutral-600">
                        REVIEWED PER REFUND POLICY · 0–45 WORKING DAYS
                      </p>
                    </div>
                  );
                })()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
